import "./creationwizard.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { debounce } from "throttle-debounce";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { type RootState } from "../../Reducers/ShopfrontReducers";
import type PageRuleScript from "../../Utilities/Domain/PageRules/PageRuleScript";
import { uppercaseFirst } from "../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getLogLevel, LogLevel } from "../../Utilities/Platform/Logging";
import PageRuleAPIHandler from "../PageRules/PageRuleAPIHandler";
import PopupManager from "../Popups/PopupManager";
import CreationWizardPage, { type WizardElement } from "./CreationWizardPage";
import CreationWizardProgress from "./CreationWizardProgress";

function mapStateToProps(state: RootState): {
    user: false | string;
    outlet: {
        id: string | null;
        name: string | null;
    };
    register: {
        id: string | null;
        name: string | null;
    };
} {
    return {
        user  : state.user.id,
        outlet: {
            id  : state.location.outlet?.id || null,
            name: state.location.outlet?.name || null,
        },
        register: {
            id  : state.location.register?.id || null,
            name: state.location.register?.name || null,
        },
    };
}

const connector = connect(mapStateToProps);

interface Props extends ConnectedProps<typeof connector> {
    onBack: (e?: React.MouseEvent) => void;
    onSkip: (e?: React.MouseEvent) => void;
    onComplete: (e?: React.MouseEvent) => void;
    onFieldChange: (field: string, value: any) => void;
    pageRule: PageRuleScript;
    args?: {};
    onRedirect?: (to: string) => boolean;
}

interface State {
    transition: boolean;
    loaded: boolean;
    backwards: boolean;
    valid: boolean;
    user: {
        name: string;
        username: string;
        role: {
            id: string | null;
        };
        permissions: Array<string>;
    };
    question: string;
    elements: Array<WizardElement>;
    previousQuestion: string;
    previousElements: Array<WizardElement>;
    metaData: any;
    pages: Array<{
        question: string;
        elements: Array<{
            field: string;
            value: string;
        }>;
        metaData: any;
    }>;
    pageCount: number;
    currentPage: number;
    title: string;
    exitTransition: boolean;
    showLoading: boolean;
    currentLocation: {
        outlet: {
            id: string | null;
            name: string | null;
        } | null;
        register: {
            id: string | null;
            name: string | null;
        } | null;
    };
    creationId: number;
}

class CreationWizard extends React.Component<Props, State> {
    protected wizardRef = React.createRef<HTMLDivElement | null>();
    protected transitionTimeout: null | ReturnType<typeof setTimeout> = null;
    protected exitTransitionTimeout: null | ReturnType<typeof setTimeout> = null;
    protected loadingTimeout: null | ReturnType<typeof setTimeout> = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            transition: false,
            loaded    : false,
            backwards : false,
            user      : {
                name    : "",
                username: "",
                role    : {
                    id: null,
                },
                permissions: [],
            },
            pageCount       : 0,
            currentPage     : 0,
            question        : "",
            title           : "Create",
            elements        : [],
            pages           : [],
            metaData        : null,
            previousQuestion: "",
            previousElements: [],
            exitTransition  : false,
            showLoading     : false,
            valid           : false,
            currentLocation : {
                outlet  : null,
                register: null,
            },
            creationId: Date.now(),
        };

        this.handlePageChange = this.handlePageChange.bind(this);
        this.handleNextPage = this.handleNextPage.bind(this);
        this.handlePreviousPage = this.handlePreviousPage.bind(this);
        this.handleFieldUpdate = this.handleFieldUpdate.bind(this);
        this.handleFieldCreate = this.handleFieldCreate.bind(this);
        this.handleAPIMessage = this.handleAPIMessage.bind(this);
        this.handleValidityCheck = debounce(100, this.handleValidityCheck.bind(this));
    }

    public componentDidMount(): void {
        this.addAPIListener();
        this.updateUser();
        this.updateCurrentLocation();
        this.handlePageChange(0, false);
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(this.props.user !== prevProps.user) {
            this.updateUser();
        }

        if(this.props.outlet?.id !== prevProps.outlet?.id || this.props.register?.id !== prevProps.register?.id) {
            this.updateCurrentLocation();
        }
    }

    public componentWillUnmount(): void {
        this.removeAPIListener();

        // Clear timeouts
        if(this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
        }

        if(this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
        }

        if(this.exitTransitionTimeout) {
            clearTimeout(this.exitTransitionTimeout);
        }

        (this.handleValidityCheck as any).cancel({
            upcomingOnly: true,
        });
    }

    protected addAPIListener(): void {
        PageRuleAPIHandler.addListener(this.handleAPIMessage);

        if(typeof this.props.onRedirect === "function") {
            PageRuleAPIHandler.addRedirectCheck(this.props.onRedirect);
        }
    }

    protected removeAPIListener(): void {
        PageRuleAPIHandler.removeListener(this.handleAPIMessage);

        if(typeof this.props.onRedirect === "function") {
            PageRuleAPIHandler.removeRedirectCheck();
        }
    }

    protected updateCurrentLocation(): void {
        this.setState({
            currentLocation: {
                outlet: {
                    id  : this.props.outlet?.id || null,
                    name: this.props.outlet?.name || null,
                },
                register: {
                    id  : this.props.register?.id || null,
                    name: this.props.register?.name || null,
                },
            },
        });
    }

    protected async updateUser(): Promise<void> {
        if(!this.props.user) {
            this.setState({
                user: {
                    name    : "Unknown",
                    username: "unknown",
                    role    : {
                        id: null,
                    },
                    permissions: [],
                },
            });

            return;
        }

        const source = await determineDataSource();
        const user = await source.users.get(this.props.user) as any;

        this.setState({
            user: {
                name    : user.name,
                username: user.username,
                role    : {
                    id: user.role_id,
                },
                permissions: user.permissions,
            },
        });
    }

    protected handleAPIMessage(type: string, data: any): void {
        switch(type) {
            case "field":
                this.props.onFieldChange(data.field, data.value);

                return;
            case "back":
                this.handlePreviousPage();

                return;
            case "next":
                this.handleNextPage();

                return;
            case "finish":
                this.props.onComplete();

                return;
            default:
                throw new Error(`API Message of type "${type}" not yet implemented`);
        }
    }

    protected cleanElements(elements: Array<WizardElement>): Array<WizardElement> {
        for(let i = 0, l = elements.length; i < l; i++) {
            if(!elements[i].default) {
                elements[i].default = "";
            }
        }

        return elements;
    }

    protected prettyField(field: string): string {
        const split = field.split(/_|(?=[A-Z])/g);

        for(let i = 0, l = split.length; i < l; i++) {
            split[i] = uppercaseFirst(split[i]);
        }

        return split.join(" ");
    }

    protected handlePageChange(pageIndex: number, transition = true): void {
        if(transition) {
            this.setState({
                transition: true,
                backwards : this.state.currentPage > pageIndex,
            });

            if(this.transitionTimeout) {
                clearTimeout(this.transitionTimeout);
            }

            this.transitionTimeout = setTimeout(() => {
                this.setState({
                    transition: false,
                });
            }, 301);
        }

        if(this.exitTransitionTimeout) {
            clearTimeout(this.exitTransitionTimeout);
        }

        let resultProcessed = false;

        this.exitTransitionTimeout = setTimeout(() => {
            this.setState({
                exitTransition: false,
            });

            if(this.loadingTimeout) {
                clearTimeout(this.loadingTimeout);
            }

            if(!resultProcessed) {
                this.loadingTimeout = setTimeout(() => {
                    this.setState({
                        showLoading: true,
                    });
                }, 300);
            }
        }, 301);

        this.setState((prevState): any => {
            if(!prevState.question) {
                // Ensure we don't update blank pages
                return {
                    currentPage: pageIndex,
                };
            }

            let pages;

            if(prevState.currentPage > pageIndex) {
                pages = prevState.pages;
                /*
                pages = [
                    ...prevState.pages.slice(0, -1),
                ];
                */
            } else {
                const elements = [];

                for(let i = 0, l = prevState.elements.length; i < l; i++) {
                    elements.push({
                        field: prevState.elements[i].field,
                        value: prevState.elements[i].__label ? {
                            label: prevState.elements[i].__label,
                            value: prevState.elements[i].default,
                        } : prevState.elements[i].default,
                    });
                }

                const newPage = {
                    question: prevState.question,
                    metaData: prevState.metaData,
                    elements,
                };

                if(
                    typeof prevState.pages[prevState.currentPage] !== "undefined" &&
                    prevState.pages[prevState.currentPage].question === prevState.question
                ) {
                    pages = [
                        ...prevState.pages.slice(0, prevState.currentPage),
                        newPage,
                        ...prevState.pages.slice(prevState.currentPage + 1),
                    ];

                    /*
                    pages = [
                        ...prevState.pages.slice(0, -1),
                        newPage,
                    ];
                    */
                } else {
                    pages = [
                        ...prevState.pages,
                        newPage,
                    ];
                }
            }

            return {
                pages,
                currentPage     : pageIndex,
                previousQuestion: prevState.question,
                previousElements: prevState.elements,
                loaded          : false,
                exitTransition  : true,
            };
        }, async () => {
            let context = {
                currentQuestion: this.state.currentPage,
                user           : this.state.user,
                answers        : this.state.pages,
                currentLocation: this.state.currentLocation,
                creationId     : this.state.creationId,
            };

            if(this.props.args) {
                context = Object.assign(context, this.props.args);
            }

            try {
                const result = await this.props.pageRule.run(context);

                resultProcessed = true;

                if(this.loadingTimeout) {
                    clearTimeout(this.loadingTimeout);
                }

                this.setState({
                    question   : result.question,
                    title      : result.title,
                    elements   : this.cleanElements(result.elements),
                    metaData   : result.metaData,
                    currentPage: pageIndex,
                    pageCount  : pageIndex + result.questionsLeft + 1,
                    loaded     : true,
                    showLoading: false,
                }, () => {
                    this.setState({
                        valid: this.checkValidity(false),
                    });
                });
            } catch(e) {
                if(e === false) {
                    if(getLogLevel() === LogLevel.VERBOSE) {
                        console.log("Skipping page rule");
                    }
                } else {
                    PopupManager.error("Page rule was skipped because of syntax error");
                    console.error("We could not execute the page rule, reason:", e);
                }

                this.props.onSkip();
            }
        });
    }

    protected handleFieldUpdate(field: string, value: any, label?: string, callback?: () => void): void {
        this.props.onFieldChange(field, value);

        this.setState((prevState) => {
            const elements = prevState.elements;

            for(let i = 0, l = elements.length; i < l; i++) {
                if(elements[i].field === field) {
                    elements[i].default = value;

                    if(label) {
                        elements[i].__label = label;
                    }

                    break;
                }
            }

            return {
                elements,
            };
        }, () => {
            if(typeof callback === "function") {
                callback();
            }

            this.handleValidityCheck();
        });
    }

    protected handleFieldCreate(field: string, value: string): void {
        this.props.onFieldChange(field, value);
    }

    protected checkValidity(displayErrors = true): boolean {
        // Ensure the page is valid
        let valid = true;

        for(let i = 0, l = this.state.elements.length; i < l; i++) {
            const value = this.state.elements[i].default;

            let printName: string;

            if(this.state.elements[i].printName) {
                printName = this.state.elements[i].printName as string;
            } else {
                printName = this.prettyField(this.state.elements[i].field);
            }

            if(this.state.elements[i].required) {
                if((typeof value === "string" && !value.trim()) || (Array.isArray(value) && !value.length)) {
                    if(displayErrors) {
                        PopupManager.error(`${printName} is required`);
                        valid = false;

                        continue;
                    } else {
                        return false;
                    }
                }
            }

            if(
                (
                    this.state.elements[i].type === "number" ||
                    this.state.elements[i].type === "currency" ||
                    this.state.elements[i].type === "percentage"
                ) && (
                    (typeof value === "string" && !!value.trim()) ||
                    typeof value === "number"
                )
            ) {
                const { min, max, step } = this.state.elements[i];

                const parsed = parseFloat(value);

                const hasStep = typeof step === "number";
                const hasMax = typeof max === "number";
                const hasMin = typeof min === "number";

                if(isNaN(parsed) && (hasStep || hasMax || hasMin)) {
                    if(displayErrors) {
                        PopupManager.error(`${printName} requires a valid number`);
                        valid = false;

                        continue;
                    } else {
                        return false;
                    }
                }

                if(hasStep && (parsed / step) % 1 !== 0) {
                    if(displayErrors) {
                        if(step === 1) {
                            PopupManager.error(`${printName} must be a whole number`);
                        } else {
                            PopupManager.error(`${printName} must be a multiple of ${step}`);
                        }

                        valid = false;

                        continue;
                    } else {
                        return false;
                    }
                }

                if(hasMax && parsed > max) {
                    if(displayErrors) {
                        PopupManager.error(`${printName} must be less than or equal to ${max}`);
                        valid = false;

                        continue;
                    } else {
                        return false;
                    }
                }

                if(hasMin && parsed < min) {
                    if(displayErrors) {
                        PopupManager.error(`${printName} must be greater than or equal to ${min}`);
                        valid = false;
                    } else {
                        return false;
                    }
                }
            }
        }

        return valid;
    }

    protected handleValidityCheck(): void {
        this.setState({
            valid: this.checkValidity(false),
        });
    }

    protected handleNextPage(): void {
        // Ensure the page is valid
        const valid = this.checkValidity();

        if(!valid) {
            return;
        }

        // Check to see if last page
        if(this.state.currentPage + 1 === this.state.pageCount) {
            this.props.onComplete();

            return;
        }

        // Move to next page
        this.handlePageChange(this.state.currentPage + 1);
    }

    protected handlePreviousPage(): void {
        // Move to previous page
        this.handlePageChange(this.state.currentPage - 1);
    }

    public render(): React.ReactNode {
        return (
            <div
                ref={this.wizardRef}
                className="creation-wizard"
            >
                <div className="creation-wizard-top">
                    <CreationWizardProgress
                        currentPage={this.state.currentPage + 1}
                        totalPages={this.state.pageCount}
                        onClick={this.handlePageChange}
                    />
                    <div className="creation-wizard-back" onClick={this.props.onBack}>
                        <FontAwesomeLight icon="times" />Cancel
                    </div>
                </div>
                <div className="creation-wizard-title">
                    <p>{this.state.title}</p>
                </div>
                <div className="creation-wizard-page-outer">
                    {this.state.showLoading && (
                        <div className="creation-wizard-pages creation-wizard-loading">
                            <p>We&apos;re loading the next question...</p>
                            <FontAwesomeLight icon="circle-notch" spin />
                        </div>
                    )}
                    {this.state.exitTransition && (
                        <div className="creation-wizard-pages creation-wizard-outgoing">
                            <CreationWizardPage
                                loaded
                                exiting={this.state.exitTransition}
                                backwards={this.state.backwards}
                                question={this.state.previousQuestion}
                                elements={this.state.previousElements}
                                onChange={this.handleFieldUpdate}
                                onCreate={this.handleFieldCreate}
                                onNext={this.handleNextPage}
                                container={this.wizardRef}
                                creationId={this.state.creationId}
                            />
                        </div>
                    )}
                    {!this.state.exitTransition && this.state.loaded && (
                        <div className="creation-wizard-pages">
                            <CreationWizardPage
                                loaded={this.state.loaded}
                                exiting={this.state.transition}
                                backwards={this.state.backwards}
                                question={this.state.question}
                                elements={this.state.elements}
                                onChange={this.handleFieldUpdate}
                                onCreate={this.handleFieldCreate}
                                onNext={this.handleNextPage}
                                container={this.wizardRef}
                                creationId={this.state.creationId}
                            />
                        </div>
                    )}
                </div>
                <div className="creation-wizard-controls">
                    {this.state.currentPage !== 0 && (
                        <div
                            className="creation-wizard-control creation-wizard-previous"
                            onClick={this.handlePreviousPage}
                            data-testid="creation-wizard-previous"
                        >
                            <FontAwesomeLight icon="long-arrow-left" /> Previous
                        </div>
                    )}
                    <div
                        className="creation-wizard-control creation-wizard-skip"
                        onClick={this.props.onSkip}
                        data-testid="creation-wizard-skip"
                    >
                        Skip
                    </div>
                    {this.state.currentPage + 1 !== this.state.pageCount && (
                        <div
                            className={"creation-wizard-control creation-wizard-next " + (
                                this.state.valid ?
                                    "valid" :
                                    "invalid"
                            )}
                            onClick={this.handleNextPage}
                            data-testid="creation-wizard-next"
                        >
                            Next <FontAwesomeLight icon="long-arrow-right" />
                        </div>
                    )}
                    {this.state.currentPage + 1 === this.state.pageCount && (
                        <div
                            className={"creation-wizard-control creation-wizard-complete " + (
                                this.state.valid ?
                                    "valid" :
                                    "invalid"
                            )}
                            onClick={this.handleNextPage}
                            data-testid="creation-wizard-finish"
                        >
                            Finish <FontAwesomeLight icon="check" />
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default connector(CreationWizard);
